import { useCallback, useState } from "react";
import classes from "./classes.module.scss";
import VoteTag from "./VoteTag";
import Typography from "components/elements/Typography";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import I18n from "components/materials/I18n";
import VoteOption from "./VoteOption";
import VoteService from "services/VoteService";
import { container } from "tsyringe";
import VoteCreateRequestResource from "common/resources/Vote/VoteCreateRequestResource";
import Form, { useForm } from "components/materials/Form";
import assert from "assert";
import { VoteWithResults } from "hooks/useVote";

const voteService = container.resolve(VoteService);

type IProps = {
	voteWithResults: VoteWithResults;
	showResults: boolean;
	onVote: () => void;
};

function getTruncatedContent(content: string, seeMore: boolean, maxLength: number = 115): string {
	return content.length > maxLength && !seeMore ? `${content.slice(0, maxLength)}...` : content;
}

export default function VoteCard(props: IProps) {
	const { question } = props.voteWithResults;
	const { clearErrors, errors, handleError } = useForm();
	const { seeMore, toggleSeeMore } = useSeeMore();
	const { submitButtonDisabled, enableSubmitButton } = useSubmitButton();

	const onSubmit = useCallback(
		async (formData: { [key: string]: unknown }) => {
			clearErrors();
			const choiceId = formData["vote"];
			assert(choiceId, "ChoiceId must be defined");
			assert(typeof choiceId === "string", "ChoiceId must be a string");

			try {
				const voteCreateRequestResource = VoteCreateRequestResource.hydrate<VoteCreateRequestResource>({ choiceId, questionId: question.id });
				await voteCreateRequestResource.validateOrReject();

				await voteService.create(voteCreateRequestResource);
				props.onVote?.();
			} catch (error) {
				handleError(error);
			}
		},
		[question, props, clearErrors, handleError],
	);

	const isVoteExpired = Boolean(question.endsAt && new Date(question.endsAt) < new Date());
	const showResults = props.showResults || isVoteExpired;

	return (
		<div className={classes["root"]}>
			<div className={classes["header"]}>{question.endsAt && <VoteTag endDate={question.endsAt} />}</div>

			<Form className={classes["content-container"]} onSubmit={onSubmit} onChange={enableSubmitButton} errors={errors}>
				<div className={classes["description-container"]}>
					<Typography type="p" size="large" weight="medium" className={classes["content"]}>
						{getTruncatedContent(question.content, seeMore)}
					</Typography>

					{question.content.length > 115 && (
						<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={toggleSeeMore}>
							{!seeMore && I18n.asset.pages.professional_space.tabs.votes.see_more}
							{seeMore && I18n.asset.pages.professional_space.tabs.votes.see_less}
						</Button>
					)}
				</div>

				<div className={classes["vote-options-container"]}>
					{question.choices.map((choice) => (
						<VoteOption key={choice.id} choice={choice} voteWithResults={props.voteWithResults} showResults={showResults} />
					))}
				</div>

				{!showResults && (
					<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} type="submit" disabled={submitButtonDisabled}>
						{I18n.asset.pages.professional_space.tabs.votes.confirm_vote}
					</Button>
				)}
			</Form>
		</div>
	);
}

const useSeeMore = () => {
	const [seeMore, setSeeMore] = useState(false);

	const toggleSeeMore = useCallback(() => setSeeMore((prev) => !prev), []);

	return { seeMore, toggleSeeMore };
};

const useSubmitButton = () => {
	const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);

	const enableSubmitButton = useCallback(() => {
		if (!submitButtonDisabled) return;
		setSubmitButtonDisabled(false);
	}, [submitButtonDisabled]);

	return { submitButtonDisabled, enableSubmitButton };
};
